/**
 * Vehicle Search (MMY) — shared Alpine form + header modal.
 * Relies on global bolt-index helpers from snippets/search_api.liquid:
 *   get_vehicle_makes, get_vehicle_models, get_vehicle_years, get_vehicle_by_id
 */
(function () {
  const getConfig = () => window.__lunoVehicleSearch || {};

  const getStrings = () => {
    const s = getConfig().strings || {};
    return {
      triggerLabel: s.triggerLabel || 'Search By Vehicle',
      modalHeading: s.modalHeading || 'Sleep better out there.',
      modalSubheading: s.modalSubheading || 'Find gear for your vehicle.',
      makePlaceholder: s.makePlaceholder || 'Make',
      modelPlaceholder: s.modelPlaceholder || 'Model',
      yearPlaceholder: s.yearPlaceholder || 'Year',
      searchButton: s.searchButton || 'Search',
      findYourFit: s.findYourFit || 'Find Your Fit',
      inlineLabel: s.inlineLabel || 'Search Your Vehicle:',
      shopAllInstead: s.shopAllInstead || 'Shop All Gear Instead',
      close: s.close || 'Close',
      loading: s.loading || 'Loading…',
      errorGeneric: s.errorGeneric || 'Something went wrong. Please try again.',
      selectMakeFirst: s.selectMakeFirst || 'Model',
      selectModelFirst: s.selectModelFirst || 'Year',
    };
  };

  /** Clears the tab's saved MMY. Not called on load — the selection should survive navigation. */
  const clearStoredVehicle = () => {
    if (window.lunoSelectedVehicle && typeof window.lunoSelectedVehicle.clear === 'function') {
      window.lunoSelectedVehicle.clear();
      return;
    }
    try {
      window.sessionStorage.removeItem('luno:selected-vehicle');
      window.localStorage.removeItem('luno:selected-vehicle');
    } catch (err) {
      // private mode — ignore
    }
  };

  const formatLabel = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\b([a-z])/g, (char) => char.toUpperCase());
  };

  const mapOptions = (list, labelKey, valueKey) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((item) => ({
        value: String(item[valueKey] != null ? item[valueKey] : item.id || ''),
        label: formatLabel(item[labelKey] != null ? item[labelKey] : item.label || ''),
      }))
      .filter((item) => item.value && item.label);
  };

  const buildResultsUrl = ({ make, model, year, vid }) => {
    const handle = getConfig().resultsCollectionHandle || 'all-vehicle-results';
    const params = new URLSearchParams();
    params.set('view', 'vehicle-results');
    if (make) params.set('make', make);
    if (model) params.set('model', model);
    if (year) params.set('year', year);
    if (vid) params.set('vid', String(vid));
    return `/collections/${encodeURIComponent(handle)}?${params.toString()}`;
  };

  const POPUP_SEARCH_LOCATION = {
    header: 'header_click_popup',
    results: 'results_page_click_popup',
  };

  const normalizeAnalyticsValue = (value, max = 120) =>
    String(value || '')
      .trim()
      .slice(0, max);

  const resolvePopupSearchLocation = (value) => {
    if (value === POPUP_SEARCH_LOCATION.results) return POPUP_SEARCH_LOCATION.results;
    return POPUP_SEARCH_LOCATION.header;
  };

  const PENDING_SUBMIT_KEY = 'luno:vehicle-search:pending-popup-submit';
  const PENDING_SUBMIT_MAX_AGE_MS = 120000;

  /** dataLayer (Elevar/GTM) + gtag (Shopify GA4), matching header search. */
  const pushPopupAnalytics = (obj, gtagExtras) => {
    if (!obj || typeof obj.event !== 'string' || !obj.event) return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(obj);
      window.__lunoVehicleSearchLastAnalytics = obj;
    } catch (err) {
      // analytics must never block search
    }
    try {
      if (typeof window.gtag !== 'function') return;
      const params = {};
      Object.keys(obj).forEach((key) => {
        if (key === 'event') return;
        params[key] = obj[key];
      });
      if (gtagExtras && typeof gtagExtras === 'object') {
        Object.keys(gtagExtras).forEach((key) => {
          params[key] = gtagExtras[key];
        });
      }
      window.gtag('event', obj.event, params);
    } catch (err) {
      // analytics must never block search
    }
  };

  const trackPopupOpen = (searchLocation) => {
    pushPopupAnalytics({
      event: 'luno_vehicle_search_popup_open',
      search_location: resolvePopupSearchLocation(searchLocation),
    });
  };

  const buildPopupSubmitPayload = ({ searchLocation, make, model, year }) => ({
    event: 'luno_vehicle_search_popup_submit',
    search_location: resolvePopupSearchLocation(searchLocation),
    vehicle_make: normalizeAnalyticsValue(make),
    vehicle_model: normalizeAnalyticsValue(model),
    vehicle_year: normalizeAnalyticsValue(year, 20),
  });

  const isVehicleResultsPage = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'vehicle-results') return true;
    return /\/collections\/[^/]*vehicle-results/.test(window.location.pathname);
  };

  const queuePopupSubmitAnalytics = (payload) => {
    try {
      window.sessionStorage.setItem(
        PENDING_SUBMIT_KEY,
        JSON.stringify({
          search_location: payload.search_location,
          vehicle_make: payload.vehicle_make,
          vehicle_model: payload.vehicle_model,
          vehicle_year: payload.vehicle_year,
          ts: Date.now(),
        }),
      );
      return true;
    } catch (err) {
      return false;
    }
  };

  const flushPendingPopupSubmitAnalytics = () => {
    if (!isVehicleResultsPage()) return;
    let pending = null;
    try {
      const raw = window.sessionStorage.getItem(PENDING_SUBMIT_KEY);
      if (!raw) return;
      window.sessionStorage.removeItem(PENDING_SUBMIT_KEY);
      pending = JSON.parse(raw);
    } catch (err) {
      return;
    }
    if (!pending || Date.now() - Number(pending.ts || 0) > PENDING_SUBMIT_MAX_AGE_MS) return;
    pushPopupAnalytics({
      event: 'luno_vehicle_search_popup_submit',
      search_location: resolvePopupSearchLocation(pending.search_location),
      vehicle_make: normalizeAnalyticsValue(pending.vehicle_make),
      vehicle_model: normalizeAnalyticsValue(pending.vehicle_model),
      vehicle_year: normalizeAnalyticsValue(pending.vehicle_year, 20),
    });
  };

  const navigateAfterPopupAnalytics = (payload, url) => {
    let navigated = false;
    const go = () => {
      if (navigated) return;
      navigated = true;
      window.location.href = url;
    };

    pushPopupAnalytics(payload, {
      event_callback: go,
      event_timeout: 2000,
    });
    window.setTimeout(go, 400);
  };

  /** Shared MMY select/submit state used by inline forms and the header modal. */
  const createVehicleSearchFormState = () => ({
    makes: [],
    models: [],
    years: [],
    selectedMakeId: '',
    selectedModelId: '',
    selectedYearId: '',
    loadingMakes: false,
    loadingModels: false,
    loadingYears: false,
    submitting: false,
    errorMessage: '',
    isRestoring: false,
    _restoreGeneration: 0,
    analyticsSource: 'homepage',
    strings: getStrings(),
    shopAllUrl: getConfig().shopAllUrl || '/collections/all-car-camping-gear',
    makesLoaded: false,

    selectionAnalyticsSource() {
      return this.analyticsSource || 'homepage';
    },

    persistSelection() {
      if (this.isRestoring || !window.lunoSelectedVehicle) return;
      window.lunoSelectedVehicle.set({
        make: this.selectedMakeLabel(),
        model: this.selectedModelLabel(),
        year: this.selectedYearLabel(),
        makeId: this.selectedMakeId,
        modelId: this.selectedModelId,
        yearId: this.selectedYearId,
      }, this.selectionAnalyticsSource());
    },

    forgetSelection() {
      if (this.isRestoring || !window.lunoSelectedVehicle) return;
      window.lunoSelectedVehicle.clear();
    },

    async restoreSavedVehicle() {
      const saved = window.lunoSelectedVehicle && window.lunoSelectedVehicle.get();
      if (!saved) return false;

      this._restoreGeneration += 1;
      const generation = this._restoreGeneration;
      this.isRestoring = true;

      const stillCurrent = () => this._restoreGeneration === generation;

      try {
        await this.ensureMakes();
        if (!stillCurrent()) return false;

        const make = this.makes.find((item) => String(item.value) === String(saved.makeId));
        if (!make) return false;

        this.selectedMakeId = String(make.value);
        await this.loadModels();
        if (!stillCurrent()) return false;

        const model = this.models.find((item) => String(item.value) === String(saved.modelId));
        if (!model) {
          this.resetSelection();
          return false;
        }

        if (typeof this.$nextTick === 'function') await this.$nextTick();
        this.selectedModelId = String(model.value);
        await this.loadYears();
        if (!stillCurrent()) return false;

        const year = this.years.find((item) => String(item.value) === String(saved.yearId));
        if (!year) {
          this.resetSelection();
          return false;
        }

        if (typeof this.$nextTick === 'function') await this.$nextTick();
        this.selectedYearId = String(year.value);
        return true;
      } finally {
        if (stillCurrent()) this.isRestoring = false;
      }
    },

    resetSelection() {
      this.selectedMakeId = '';
      this.selectedModelId = '';
      this.selectedYearId = '';
      this.models = [];
      this.years = [];
      this.errorMessage = '';
    },

    async ensureMakes() {
      if (this.makesLoaded || this.loadingMakes) return;
      if (typeof window.get_vehicle_makes !== 'function') {
        this.errorMessage = this.strings.errorGeneric;
        return;
      }
      this.loadingMakes = true;
      try {
        const data = await window.get_vehicle_makes();
        this.makes = mapOptions(data, 'make', 'id');
        this.makesLoaded = true;
      } catch (err) {
        this.errorMessage = this.strings.errorGeneric;
        this.makes = [];
      } finally {
        this.loadingMakes = false;
      }
    },

    async onMakeChange() {
      if (this.isRestoring) return;
      this._restoreGeneration += 1;
      this.forgetSelection();
      this.errorMessage = '';
      this.selectedModelId = '';
      this.selectedYearId = '';
      this.models = [];
      this.years = [];
      if (!this.selectedMakeId) return;
      await this.loadModels();
    },

    async onModelChange() {
      if (this.isRestoring) return;
      this._restoreGeneration += 1;
      this.forgetSelection();
      this.errorMessage = '';
      this.selectedYearId = '';
      this.years = [];
      if (!this.selectedModelId) return;
      await this.loadYears();
    },

    onYearChange() {
      if (this.isRestoring) return;
      this.errorMessage = '';
      this.persistSelection();
    },

    async loadModels() {
      if (!this.selectedMakeId || typeof window.get_vehicle_models !== 'function') return;
      this.loadingModels = true;
      try {
        const data = await window.get_vehicle_models(this.selectedMakeId);
        this.models = mapOptions(data, 'model', 'id').filter((m) => m.label.length > 0);
      } catch (err) {
        this.errorMessage = this.strings.errorGeneric;
        this.models = [];
      } finally {
        this.loadingModels = false;
      }
    },

    async loadYears() {
      if (!this.selectedModelId || typeof window.get_vehicle_years !== 'function') return;
      this.loadingYears = true;
      try {
        const data = await window.get_vehicle_years(this.selectedModelId);
        this.years = mapOptions(data, 'year', 'id');
      } catch (err) {
        this.errorMessage = this.strings.errorGeneric;
        this.years = [];
      } finally {
        this.loadingYears = false;
      }
    },

    selectedMakeLabel() {
      return this.makes.find((m) => String(m.value) === String(this.selectedMakeId))?.label || '';
    },

    selectedModelLabel() {
      return this.models.find((m) => String(m.value) === String(this.selectedModelId))?.label || '';
    },

    selectedYearLabel() {
      return this.years.find((y) => String(y.value) === String(this.selectedYearId))?.label || '';
    },

    submit(options) {
      if (!this.canSubmit || this.submitting) return null;
      this.persistSelection();
      this.submitting = true;
      this.errorMessage = '';

      const skipNavigate = !!(options && options.skipNavigate);

      const vehicle = {
        make: this.selectedMakeLabel(),
        model: this.selectedModelLabel(),
        year: this.selectedYearLabel(),
        makeId: this.selectedMakeId,
        modelId: this.selectedModelId,
        yearId: this.selectedYearId,
        vid: this.selectedYearId,
      };

      const url = buildResultsUrl({
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        vid: vehicle.vid,
      });

      window.dispatchEvent(
        new CustomEvent('luno:vehicle-search:submitted', {
          bubbles: true,
          detail: { ...vehicle, url },
        }),
      );

      if (!skipNavigate) {
        window.location.href = url;
      }
      return { vehicle, url };
    },
  });

  const registerVehicleSearchComponents = () => {
    if (!window.Alpine || typeof window.Alpine.data !== 'function') return;
    if (window.__lunoVehicleSearchComponentsRegistered) return;
    window.__lunoVehicleSearchComponentsRegistered = true;

    window.Alpine.data('vehicleSearchForm', () => ({
      ...createVehicleSearchFormState(),

      get canSubmit() {
        return !!(this.selectedMakeId && this.selectedModelId && this.selectedYearId);
      },

      async init() {
        await this.ensureMakes();
        await this.restoreSavedVehicle();
      },
    }));

    window.Alpine.data('vehicleSearchModal', () => {
      const formState = createVehicleSearchFormState();
      const formSubmit = formState.submit;

      return {
        ...formState,
        open: false,
        previousFocus: null,
        searchLocation: POPUP_SEARCH_LOCATION.header,

        selectionAnalyticsSource() {
          if (this.searchLocation === POPUP_SEARCH_LOCATION.results) return 'results_page';
          return 'header';
        },

        get canSubmit() {
          return !!(this.selectedMakeId && this.selectedModelId && this.selectedYearId);
        },

        submit() {
          if (!this.canSubmit || this.submitting) return;

          const payload = buildPopupSubmitPayload({
            searchLocation: this.searchLocation,
            make: this.selectedMakeLabel(),
            model: this.selectedModelLabel(),
            year: this.selectedYearLabel(),
          });

          const queued = queuePopupSubmitAnalytics(payload);
          if (!queued) {
            const result = formSubmit.call(this, { skipNavigate: true });
            if (!result) return;
            navigateAfterPopupAnalytics(payload, result.url);
            return;
          }

          formSubmit.call(this);
        },

        init() {
          window.addEventListener('luno:vehicle-search:open', (event) => {
            const detail = event && event.detail ? event.detail : {};
            this.openModal({ location: detail.location });
          });
          document.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-vehicle-search-trigger]');
            if (!trigger) return;
            event.preventDefault();
            this.openModal({
              location: trigger.getAttribute('data-vehicle-search-location'),
            });
          });

          this.$watch('open', (isOpen) => {
            document.querySelectorAll('[data-vehicle-search-trigger]').forEach((el) => {
              el.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            });
            const body = document.body;
            if (body && body._x_dataStack && body._x_dataStack[0]) {
              body._x_dataStack[0].lockScroll = isOpen;
            } else if (body) {
              body.classList.toggle('tw-overflow-hidden', isOpen);
            }
          });
        },

        async openModal({ location } = {}) {
          const searchLocation = resolvePopupSearchLocation(location);
          if (!this.open) {
            this.searchLocation = searchLocation;
            trackPopupOpen(searchLocation);
          }
          this.previousFocus = document.activeElement;
          this.open = true;
          const restored = await this.restoreSavedVehicle();
          if (!restored) this.resetSelection();
          await this.ensureMakes();
          this.$nextTick(() => {
            const first = this.$refs.makeSelect;
            if (first) first.focus();
          });
        },

        close() {
          if (!this.open) return;
          this.open = false;
          this.resetSelection();
          this.$nextTick(() => {
            if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
              this.previousFocus.focus();
            }
          });
        },
      };
    });
  };

  // Expose helpers for results page / other callers
  window.lunoVehicleSearch = window.lunoVehicleSearch || {};
  window.lunoVehicleSearch.buildResultsUrl = buildResultsUrl;
  window.lunoVehicleSearch.clearStoredVehicle = clearStoredVehicle;
  window.lunoVehicleSearch.open = () => {
    window.dispatchEvent(new CustomEvent('luno:vehicle-search:open'));
  };

  flushPendingPopupSubmitAnalytics();

  if (window.Alpine) {
    registerVehicleSearchComponents();
  }
  document.addEventListener('alpine:init', registerVehicleSearchComponents);
})();
